import React, {useState} from 'react';
import {List} from 'immutable';
import Button from '@material-ui/core/Button';
import './CoachingReviewTypePage.styl';
import {Link} from 'react-router';
import {CallToAction} from 'components/pure/tooltips/CallToAction';
import {ColorDotWithTooltip} from 'components/utils/ColorDot';
import {RadioButton} from 'material-ui';
import CoachingReviewTypeActions from 'actions/coaching/CoachingReviewTypeActions';
import CoachingMeetingTypeChooser from 'components/pure/admin/CoachingMeetingTypeChooser';
import CoachingMeetingCategory from 'records/CoachingMeetingCategory';
import CoachingReviewType from 'records/CoachingReviewType';

interface Props {
  types: List<CoachingReviewType>;
  typesLoaded: boolean;
  categories: List<CoachingMeetingCategory>;
  categoriesLoaded: boolean;
}

const CoachingReviewTypePage: React.FC<Props> = ({
  types = List<CoachingReviewType>(),
  typesLoaded,
  categories = List<CoachingMeetingCategory>(),
  categoriesLoaded,
}) => {
  const [renderMeetingCategoryChooser, setRenderMeetingCategoryChooser] = useState(false);

  function renderTable(types) {
    return (
      <div>
        <table className="review-type-table">
          <tbody>
            <tr>
              <th>Default</th>
              <th>Name</th>
              <th># Questions</th>
            </tr>
            {types.toList().map(type => (
              <tr key={type.id}>
                <td className="default-column">
                  <RadioButton
                    onCheck={() =>
                      CoachingReviewTypeActions.upsertReviewType(type.set('isDefault', true)).then(
                        () => CoachingReviewTypeActions.fetchAllReviewTypes(true)
                      )
                    }
                    checked={type.isDefault}
                  />
                </td>
                <td className="name-column">
                  <Link to={`/admin/sessionTypes/edit/${type.id}`}>
                    <ColorDotWithTooltip
                      tooltip={type.meetingCategory.name}
                      color={type.meetingCategory.color}
                      size={12}
                    />
                    <div className="generic-link">{type.name}</div>
                  </Link>
                </td>
                <td className="question-column">{type.questions.size}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      <div className="coaching-review-type-page">
        <h1>Coaching Meeting Types</h1>
        <p>
          Coaching Meeting Types allow you to customize coaching workflow by setting up different
          questions that can be asked during a Coaching Review Meeting.
        </p>
        <Button
          component={Link}
          variant="contained"
          color="primary"
          className="ev-button"
          to={'/admin/sessionTypes/create'}>
          New Coaching Meeting Type
        </Button>
        <Button
          variant="outlined"
          className="ev-button meeting-cat-chooser-btn"
          onClick={() => setRenderMeetingCategoryChooser(true)}>
          Update Visible Meeting Categories
        </Button>
        {renderTable(types)}
        {types.filter(t => !t.isDefault).isEmpty() && typesLoaded && (
          <CallToAction
            text={
              <>
                <p>
                  Coaching Meeting Types can be customized to your organization's workflow. Click on
                  "New Coaching Meeting Type" to make a new meeting type; you can define as many
                  questions as you need and specify how they should contribute to the score for each
                  meeting.
                </p>
                <p>
                  When coaches create new plans, or start a review, they'll be prompted to choose
                  which coaching meeting type they want to use. Please reach out to your Customer
                  Service Representative if you'd like a demo of how to use this feature.
                </p>
              </>
            }
          />
        )}
      </div>
      <CoachingMeetingTypeChooser
        open={renderMeetingCategoryChooser}
        categories={categories
          .map(cat => ({
            category: cat!,
            used: types.some(t => (!t || !cat ? false : t.meetingCategoryId === cat.id)),
          }))
          .toList()}
        categoriesLoaded={categoriesLoaded}
        onRequestClose={() => setRenderMeetingCategoryChooser(false)}
      />
    </>
  );
};

export default CoachingReviewTypePage;
